import { DatePickerEventDetail, InputChangeEventDetail, SelectChangeEventDetail } from '@wppopen/components-library'
import {
  WppDatepicker,
  WppIconClose,
  WppIconError,
  WppIconSearch,
  WppIconTick,
  WppIconTune,
  WppIconWarning,
  WppInput,
  WppSelect,
  WppSideModal,
} from '@wppopen/components-library-react'
// eslint-disable-next-line import/order
import {
  WppDatepickerCustomEvent,
  WppInputCustomEvent,
  WppSelectCustomEvent,
} from '@wppopen/components-library/dist/types/components'
import { AnalyticsActionType } from '@wppopen/core'
import clsx from 'clsx'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from 'components/common/button/Button'
import { countActiveFilters } from 'helpers/projectStatusHelper'
import { SyncStatus } from 'types/Project'
import { formatDate } from 'utils'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './ProjectsListFilters.module.scss'

export interface ProjectsFiltersType {
  briefType?: string
  uhubStatus?: string
  uhubProjectStatus?: string
  operatingUnit?: string
  dateSubmitted?: [string, string] | []
  targetInMarketDate?: [string, string] | []
  search?: string
  status?: string
  syncStatus?: string // Workfront submission status
}
export const DEFAULT_SIDEBAR_FILTERS: ProjectsFiltersType = {
  briefType: '',
  uhubStatus: '', // uhub phase
  uhubProjectStatus: '', // uhub status
  operatingUnit: '',
  dateSubmitted: [],
  targetInMarketDate: [],
  status: '',
  syncStatus: '',
}

export const DEFAULT_FILTERS: ProjectsFiltersType = {
  search: undefined,
  ...DEFAULT_SIDEBAR_FILTERS,
}

// !TODO!
// This is a very very ugly solution as we dont have the data defined in the backend yet, for now, we are hardcoding the filters
const hardcoded_filters = {
  briefType: [
    { label: 'Tactical Execution', value: 'Tactical Execution' },
    { label: 'Studio X Led', value: 'Studio X Led' },
    { label: 'Other Execution', value: 'Other Execution' },
    { label: 'Squad Led', value: 'Squad Led' },
    { label: 'Express Experience', value: 'Express Experience' },
    { label: 'Marketing', value: 'Marketing' },
  ],
  uhubProjectStatus: [
    { label: 'Not Started', value: 'Not Started' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Cancelled', value: 'Cancelled' },
  ],
  syncStatus: [
    { value: 'SYNCED', label: 'Synced' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'ERROR', label: 'Error' },
  ],
  uhubStatus: [
    { label: 'Pipeline', value: 'Pipeline' },
    { label: 'Assemble', value: 'Assemble' },
    { label: 'Explore', value: 'Explore' },
  ],
  status: [
    { value: 'PLN', label: 'Not started' },
    { value: 'CUR', label: 'In Progress' },
    { value: 'CPL', label: 'Completed' },
  ],
  ou: [
    { label: 'North America', value: 'North America' },
    { label: 'ASEAN & South Pacific', value: 'ASEAN & South Pacific' },
    { label: 'Greater China & Mongolia', value: 'Greater China & Mongolia' },
    { label: '**The Coca-Cola Company Global', value: '**The Coca-Cola Company Global' },
    { label: 'Japan & South Korea', value: 'Japan & South Korea' },
    { label: 'Europe', value: 'Europe' },
    { label: 'Latin America', value: 'Latin America' },
    { label: 'India & Southwest Asia', value: 'India & Southwest Asia' },
    { label: 'Africa', value: 'Africa' },
    { label: 'Eurasia & Middle East', value: 'Eurasia & Middle East' },
  ],
}

const ProjectsListFilters = forwardRef(
  (
    {
      onChange,
      warningsCount = 0,
      errorsCount = 0,
      onPillClick,
    }: {
      errorsCount?: number
      warningsCount?: number
      onChange: (value: Partial<ProjectsFiltersType>) => void
      onPillClick?: (type: SyncStatus) => void
    },
    ref,
  ) => {
    const [filters, setFilters] = useState<ProjectsFiltersType | undefined>(DEFAULT_FILTERS)
    const [search, setSearch] = useState<string | undefined>()
    const [sidebarFilters, setSidebarFilters] = useState(DEFAULT_SIDEBAR_FILTERS)

    const [query, setQuery] = useSearchParams()

    const [activeFiltersCount, setActiveFiltersCount] = useState(0)

    const [isVisible, setIsVisible] = useState(false)

    const handleSearchChange = (e?: WppInputCustomEvent<InputChangeEventDetail>) => {
      setSearch(e?.target?.value)

      setFilters(prevFilters => ({
        ...prevFilters,
        search: e?.target.value,
      }))
    }

    const handleSelectSidebarFilter = (
      key: keyof ProjectsFiltersType,
      event: WppSelectCustomEvent<SelectChangeEventDetail> | WppDatepickerCustomEvent<DatePickerEventDetail>,
    ) => {
      const selectedValues = 'date' in event?.detail ? event.detail.date : event.detail.value
      setSidebarFilters(prevFilters => ({
        ...prevFilters,
        [key]: selectedValues,
      }))
    }

    const handleSelectDateRangePicker = (
      key: keyof ProjectsFiltersType,
      event: WppDatepickerCustomEvent<DatePickerEventDetail>,
    ) => {
      const date = event.detail.date as Date[]
      setSidebarFilters(prevFilters => ({
        ...prevFilters,
        [key]: [formatDate(date[0], false, 'yyyy-MM-dd'), formatDate(date[1], false, 'yyyy-MM-dd')],
      }))
    }

    const handleApplySidebarFilters = () => {
      setFilters(prevFilters => {
        return { ...prevFilters, ...sidebarFilters }
      })
      setIsVisible(false)
      trackAnalytics({
        type: AnalyticsActionType.action,
        payload: ANALYTICS_EVENTS.FILTERS_APPLY,
      })
    }

    //eslint-disable-next-line
    useEffect(() => onChange(filters || {}), [filters])

    const setWorkfrontIdFilter = (workfrontId: string) => {
      setFilters(prevFilters => {
        return { ...prevFilters, workfrontProjectId: workfrontId }
      })
    }

    const clearAllFilters = () => {
      setFilters(DEFAULT_FILTERS)
      setSidebarFilters(DEFAULT_SIDEBAR_FILTERS)
      setSearch(undefined)

      query.delete('workfrontProjectId')
      setQuery(query)
    }

    useImperativeHandle(ref, () => ({
      clearAllFilters,
      setWorkfrontIdFilter,
    }))

    const handlePillClicked = (type: SyncStatus) => {
      onPillClick?.(type)
      if (type === filters?.syncStatus) {
        setFilters(prevFilters => ({ ...prevFilters, syncStatus: undefined }))
        setSidebarFilters(prevFilters => ({ ...prevFilters, syncStatus: undefined }))
      } else {
        setFilters(prevFilters => ({ ...prevFilters, syncStatus: type }))
        setSidebarFilters(prevFilters => ({ ...prevFilters, syncStatus: type }))
      }
    }

    useEffect(() => setActiveFiltersCount(countActiveFilters(filters)), [filters])

    return (
      <div className={styles.filtersWrapper}>
        <div className="flex gap-2">
          <div>
            <button
              onClick={() => handlePillClicked?.(SyncStatus.ERROR)}
              className={clsx(
                'hover:cursor-pointer z-1 border border-solid border-[#8B8B8B] rounded-full flex flex-row items-center justify-center h-10 px-3',
                {
                  'bg-[#e81c2333]': filters?.syncStatus === SyncStatus.ERROR,
                  ' bg-transparent': filters?.syncStatus !== SyncStatus.ERROR,
                },
              )}
            >
              <span className={clsx(styles.SBody, styles.pill, 'text-[#383838]')}>
                <WppIconError className="me-2" />
                Error ({errorsCount}){filters?.syncStatus === SyncStatus.ERROR && <WppIconTick color="#191919" />}
              </span>
            </button>
          </div>
          <div>
            <button
              onClick={() => handlePillClicked?.(SyncStatus.WARNING)}
              className={clsx(
                'hover:cursor-pointer z-1 border border-solid border-[#8B8B8B] rounded-full flex flex-row items-center justify-center h-10 px-3',
                {
                  'bg-[#FC4D0F33]': filters?.syncStatus === SyncStatus.WARNING,
                  ' bg-transparent': filters?.syncStatus !== SyncStatus.WARNING,
                },
              )}
            >
              <span className={clsx(styles.SBody, styles.pill, 'text-[#383838]')}>
                <WppIconWarning className="me-2" />
                Brief Changed ({warningsCount})
                {filters?.syncStatus === SyncStatus.IN_PROGRESS && <WppIconTick color="#191919" />}
              </span>
            </button>
          </div>
          <div className="flex">
            <button
              onClick={() => {
                setIsVisible(true)
                trackAnalytics({ type: AnalyticsActionType.action, payload: ANALYTICS_EVENTS.SORT_AND_FILTER })
              }}
              className={clsx(
                'bg-transparent hover:cursor-pointer z-1 border border-solid border-[#8B8B8B] rounded-full flex flex-row items-center justify-center h-10 px-3',
              )}
            >
              <WppIconTune width={16} color="#4C4C4C" className="mr-[10px]" />
              <span className={clsx(styles.SStrong, 'text-[#383838] whitespace-nowrap flex')}>
                <span className={clsx(styles.SStrong, styles.pill, 'text-[#383838] font-semibold')}>Filter</span>
                {!!activeFiltersCount && <span>&nbsp;({activeFiltersCount})</span>}
              </span>
            </button>
            {!!activeFiltersCount && (
              <button className="ml-2 bg-transparent hover:cursor-pointer z-1 border border-solid border-[#8B8B8B] rounded-full flex flex-row items-center justify-center h-10 px-3">
                <span
                  className={clsx(styles.SStrong, styles.pill, 'text-[#383838] font-semibold')}
                  onClick={clearAllFilters}
                >
                  Clear All
                </span>
              </button>
            )}
          </div>

          <div>
            <WppSideModal
              open={isVisible}
              className={styles.filtersSideModal}
              onWppSideModalClose={() => {
                setSidebarFilters(DEFAULT_SIDEBAR_FILTERS)
                setFilters(prevFilters => ({ ...prevFilters, ...DEFAULT_SIDEBAR_FILTERS }))
                setIsVisible(false)
              }}
              onWppSideModalOpen={() => setIsVisible(true)}
            >
              <div slot="header" className={clsx(styles.filtersHeading)}>
                Sort and Filters
              </div>
              <div slot="body" className="pb-4">
                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>Brief Type</div>
                  <WppSelect
                    className={styles.select}
                    type="single"
                    placeholder="Brief Type"
                    value={sidebarFilters?.briefType}
                    list={hardcoded_filters.briefType}
                    onWppChange={(ev: WppSelectCustomEvent<SelectChangeEventDetail>) => {
                      handleSelectSidebarFilter('briefType', ev)
                    }}
                  />
                </div>
                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>Project Sync Status</div>
                  <WppSelect
                    className={styles.select}
                    type="single"
                    placeholder="Project Sync Status"
                    value={sidebarFilters?.syncStatus}
                    list={hardcoded_filters.syncStatus}
                    required
                    onWppChange={(ev: WppSelectCustomEvent<SelectChangeEventDetail>) => {
                      handleSelectSidebarFilter('syncStatus', ev)
                    }}
                  />
                </div>

                {/* This is not working because frontend map the value, we have to move the mapping to back
                 <div className="mt-[16px]">
                  <div className={styles.selectLabel}>uHub Brief Status</div>
                  <WppSelect
                    className={styles.select}
                    placeholder="uHub Status"
                    value={sidebarFilters?.uhubProjectStatus}
                    list={hardcoded_filters.uhubProjectStatus}
                    onWppChange={(ev: WppSelectCustomEvent<SelectChangeEventDetail>) => {
                      handleSelectSidebarFilter('uhubProjectStatus', ev)
                    }}
                  />
                </div> */}
                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>uHub Phase</div>
                  <WppSelect
                    className={styles.select}
                    placeholder="uHub Phase"
                    value={sidebarFilters?.uhubStatus}
                    list={hardcoded_filters.uhubStatus}
                    onWppChange={(ev: WppSelectCustomEvent<SelectChangeEventDetail>) => {
                      handleSelectSidebarFilter('uhubStatus', ev)
                    }}
                  />
                </div>
                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>Operating Unit</div>
                  <WppSelect
                    className={styles.select}
                    placeholder="Operating Unit"
                    value={sidebarFilters?.operatingUnit}
                    list={hardcoded_filters.ou}
                    required
                    onWppChange={(ev: WppSelectCustomEvent<SelectChangeEventDetail>) => {
                      handleSelectSidebarFilter('operatingUnit', ev)
                    }}
                  />
                </div>

                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>Date submitted</div>
                  <WppDatepicker
                    range
                    locale={{ dateFormat: 'MM/dd/yyyy' }}
                    placeholder="mm/dd/yyyy - mm/dd/yyyy"
                    value={sidebarFilters?.dateSubmitted}
                    onWppChange={(ev: WppDatepickerCustomEvent<DatePickerEventDetail>) =>
                      handleSelectDateRangePicker('dateSubmitted', ev)
                    }
                    style={{
                      '--adp-cell-background-color-selected': 'red',
                      '--datepicker-current-date-color-active': 'red',
                      '--datepicker-cell-color-selected': 'red',
                    }}
                  />
                </div>

                <div className="mt-[16px]">
                  <div className={styles.selectLabel}>Target In-Market date</div>
                  <WppDatepicker
                    className="w-full"
                    range
                    locale={{
                      dateFormat: 'MM/dd/yyyy',
                    }}
                    value={sidebarFilters?.targetInMarketDate}
                    placeholder="mm/dd/yyyy - mm/dd/yyyy"
                    onWppChange={(ev: WppDatepickerCustomEvent<DatePickerEventDetail>) =>
                      handleSelectDateRangePicker('targetInMarketDate', ev)
                    }
                  />
                </div>
              </div>
              <div slot="actions">
                <div className="flex flex-row items-center justify-end">
                  <Button text="Cancel" variant="primary" onClick={() => setIsVisible(false)} />
                  <Button text="Apply filters" variant="secondary" onClick={handleApplySidebarFilters} />
                </div>
              </div>
            </WppSideModal>
          </div>
        </div>
        <div>
          <WppInput
            className={clsx(styles.input, 'w-full')}
            name="search"
            placeholder="Search by project name"
            value={search}
            onWppChange={(e: WppInputCustomEvent<InputChangeEventDetail>) => {
              handleSearchChange(e)
            }}
          >
            <WppIconSearch color="#000000" slot="icon-start" aria-label="Search icon" />
            {search && (
              <WppIconClose
                slot="icon-end"
                aria-label="Clear icon"
                onClick={() => {
                  handleSearchChange(undefined)
                }}
                color="#000000"
              />
            )}
          </WppInput>
        </div>
      </div>
    )
  },
)

export default ProjectsListFilters
